import { useState } from 'react';
import {
  useAttributes, useCategories, useCollections, useCreateCategory, useCreateCollection,
  useSetAttributeValueActive, useUpdateCategory, useUpdateCollection,
} from '../../api/catalog';
import type {
  AttributeGroup, CategoryRow, CollectionRow, CollectionState,
} from '../../api/catalog';
import { ApiError } from '../../api/client';
import { useSession } from '../../auth/session';
import {
  Alert, Button, Chip, Empty, Field, Modal, Note, PageHeader, Panel, Spinner, Tabs, Table,
  type Column,
} from '../../ui';

/**
 * A-10 — archetype 10: the taxonomy, and the option lists the product editor draws on.
 *
 * The taxonomy is configuration, not code. Adding a collection is not a release,
 * which is the whole reason this screen exists rather than a migration.
 *
 * CR-12 — one switch on an attribute value governs three places at once: the bulk
 * upload rejects an inactive value, the product editor stops offering it, and the
 * customer never sees it. **Deactivating does not delete.** Pieces already carrying
 * the value keep it and stay sellable; the value simply stops being offered from
 * here on, and reactivating brings it back. That is why this screen has no delete.
 *
 * There is no stock, availability or quantity anywhere on it, and no minimum order
 * quantity (D4, CR-07) — a collection is a grouping, not a shelf.
 */

/** A slug is stable and appears in a URL, so it is suggested rather than typed. */
function slugify(name: string): string {
  return name
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 64);
}

type Editing =
  | { kind: 'collection'; row: CollectionRow | null }
  | { kind: 'category'; row: CategoryRow | null }
  | null;

export function Collections() {
  const { can } = useSession();
  const mayEdit = can('collection.manage');

  const { data: collections, isLoading: loadingCols, error: colError } = useCollections();
  const { data: categories, isLoading: loadingCats } = useCategories();
  const { data: groups, isLoading: loadingAttrs } = useAttributes();

  const createCollection = useCreateCollection();
  const updateCollection = useUpdateCollection();
  const createCategory = useCreateCategory();
  const updateCategory = useUpdateCategory();
  const setValueActive = useSetAttributeValueActive();

  const [editing, setEditing] = useState<Editing>(null);
  const [activeGroup, setActiveGroup] = useState<string | null>(null);
  const [failed, setFailed] = useState<string | null>(null);

  if (loadingCols || loadingCats || loadingAttrs) return <Spinner />;
  if (colError) return <Alert tone="stop">{colError.message}</Alert>;

  const all = groups ?? [];
  const selected: AttributeGroup | undefined =
    all.find((g) => g.key === activeGroup) ?? all[0];

  async function run<T>(fn: () => Promise<T>, after?: () => void) {
    setFailed(null);
    try { await fn(); after?.(); }
    catch (e) { setFailed(e instanceof ApiError ? e.message : 'That did not work'); }
  }

  const collectionColumns: Column<CollectionRow>[] = [
    { header: 'Collection', render: (c) => c.name },
    { header: 'Slug', render: (c) => <code>{c.slug}</code> },
    // A count of what is grouped, not a count of what is available to sell.
    { header: 'Variants', numeric: true, render: (c) => c.variantCount },
    {
      header: 'State',
      render: (c) => (
        c.state === 'PUBLISHED' ? <Chip tone="done">Published</Chip> : <Chip tone="lapse">Draft</Chip>
      ),
    },
    {
      header: '',
      numeric: true,
      render: (c) => (
        mayEdit
          ? <Button size="sm" tone="ghost" onClick={() => setEditing({ kind: 'collection', row: c })}>Edit</Button>
          : null
      ),
    },
  ];

  const categoryColumns: Column<CategoryRow>[] = [
    { header: 'Category', render: (c) => c.name },
    { header: 'Slug', render: (c) => <code>{c.slug}</code> },
    { header: 'Variants', numeric: true, render: (c) => c.variantCount },
    {
      header: '',
      numeric: true,
      render: (c) => (
        mayEdit
          ? <Button size="sm" tone="ghost" onClick={() => setEditing({ kind: 'category', row: c })}>Edit</Button>
          : null
      ),
    },
  ];

  return (
    <>
      <PageHeader
        /*
         * "Masters setup", matching the menu. The screen holds three panels — collections,
         * categories and attribute values — and the old title named two of them, which left
         * the third looking like it belonged somewhere else.
         */
        title="Masters setup"
      />
      {failed ? <Alert tone="stop">{failed}</Alert> : null}

      <div className="mk-grid2">
        <Panel
          title="Collections"
          actions={
            mayEdit
              ? <Button size="sm" tone="ghost" onClick={() => setEditing({ kind: 'collection', row: null })}>New collection</Button>
              : null
          }
        >
          <Table
            rows={collections ?? []}
            columns={collectionColumns}
            rowKey={(c) => c.id}
            empty={<Empty title="No collections yet" body="A collection groups a line of designs. It is not required." />}
          />
        </Panel>

        <Panel
          title="Categories"
          actions={
            mayEdit
              ? <Button size="sm" tone="ghost" onClick={() => setEditing({ kind: 'category', row: null })}>New category</Button>
              : null
          }
        >
          <Table
            rows={categories ?? []}
            columns={categoryColumns}
            rowKey={(c) => c.id}
            empty={<Empty title="No categories yet" body="Rings, earrings, bracelets, necklaces — what kind of piece it is." />}
          />
        </Panel>
      </div>

      <Panel title="Attribute values">
        <div className="mk-pbody">
          <Note>
            Metal type, purity, colour and the four Cs follow published trade standards
            rather than a list somebody typed. <b>Deactivating a value does not delete
            it</b> — pieces already carrying it keep it, and it stops being offered from
            here on. Reactivating brings it back.
          </Note>
        </div>

        {all.length === 0 ? (
          <Empty title="No attribute values" body="Nothing has been loaded for this portal." />
        ) : (
          <>
            <Tabs
              tabs={all.map((g) => ({
                id: g.key,
                label: g.name,
                badge: `${g.values.filter((v) => v.isActive).length}/${g.values.length}`,
              }))}
              active={selected?.key ?? ''}
              onChange={setActiveGroup}
            />
            {selected ? (
              <AttributeValues
                group={selected}
                mayEdit={mayEdit}
                pending={setValueActive.isPending}
                onToggle={(valueId, isActive) => void run(
                  () => setValueActive.mutateAsync({ valueId, isActive }),
                )}
              />
            ) : null}
          </>
        )}
      </Panel>

      <Note>
        A value is <b>master data</b>: the frozen contract has schemas for collections
        and categories but none for creating or renaming an attribute value, so this
        screen switches one on and off and does not invent it. Adding a value needs a
        contract change.
      </Note>

      {editing?.kind === 'collection' ? (
        <CollectionEditor
          row={editing.row}
          pending={createCollection.isPending || updateCollection.isPending}
          onClose={() => setEditing(null)}
          onSave={(b) => void run(
            () => (editing.row
              ? updateCollection.mutateAsync({
                id: editing.row.id,
                body: { name: b.name, state: b.state, sortOrder: b.sortOrder },
              })
              : createCollection.mutateAsync(b)),
            () => setEditing(null),
          )}
        />
      ) : null}

      {editing?.kind === 'category' ? (
        <CategoryEditor
          row={editing.row}
          pending={createCategory.isPending || updateCategory.isPending}
          onClose={() => setEditing(null)}
          onSave={(b) => void run(
            () => (editing.row
              ? updateCategory.mutateAsync({
                id: editing.row.id,
                body: { name: b.name, sortOrder: b.sortOrder },
              })
              : createCategory.mutateAsync(b)),
            () => setEditing(null),
          )}
        />
      ) : null}
    </>
  );
}

/**
 * One group's values.
 *
 * The metal column only appears where the group actually has metal-scoped values:
 * karat is a gold measure, so a platinum purity is not a karat and a product in
 * platinum is offered no colour at all (CR-45). Where nothing is scoped, an "Any"
 * column of nothing but "Any" is noise.
 */
function AttributeValues(
  { group, mayEdit, pending, onToggle }: {
    group: AttributeGroup;
    mayEdit: boolean;
    pending: boolean;
    onToggle: (valueId: string, isActive: boolean) => void;
  },
) {
  const scoped = group.values.some((v) => v.metalTypeCode !== null);
  const activeCount = group.values.filter((v) => v.isActive).length;

  const columns: Column<AttributeGroup['values'][number]>[] = [
    { header: 'Code', width: '1%', render: (v) => <code>{v.code}</code> },
    { header: 'Shown as', render: (v) => (v.isActive ? v.label : <span className="mk-dim">{v.label}</span>) },
    ...(scoped
      ? [{
        header: 'Metal',
        render: (v: AttributeGroup['values'][number]) =>
          v.metalTypeCode ?? <span className="mk-dim">Any</span>,
      }]
      : []),
    { header: 'Standard', render: (v) => <span className="mk-dim">{v.standard ?? '—'}</span> },
    {
      header: 'Offered',
      render: (v) => (v.isActive ? <Chip tone="done">Offered</Chip> : <Chip tone="lapse">Withdrawn</Chip>),
    },
    {
      header: '',
      numeric: true,
      render: (v) => (
        mayEdit
          ? (
            <Button
              size="sm"
              tone="ghost"
              disabled={pending}
              onClick={() => onToggle(v.id, !v.isActive)}
              title={v.isActive
                ? 'Stops being offered from here on. Pieces already carrying it keep it.'
                : 'Offers it again, in the catalog, the product editor and the bulk upload.'}
            >
              {v.isActive ? 'Withdraw' : 'Offer'}
            </Button>
          )
          : null
      ),
    },
  ];

  return (
    <>
      <Table rows={group.values} columns={columns} rowKey={(v) => v.id} />
      <div className="mk-pbody">
        <Note>
          {group.name} follows <b>{group.standard}</b>. {activeCount} of {group.values.length}{' '}
          values are offered.
          {scoped
            ? ' Each value belongs to one metal type — karat is a gold measure, so a platinum purity is not a karat and a product in platinum is offered no colour.'
            : ''}
        </Note>
      </div>
    </>
  );
}

interface CollectionBody {
  name: string;
  slug: string;
  state: CollectionState;
  sortOrder: number;
}

function CollectionEditor(
  { row, pending, onClose, onSave }: {
    row: CollectionRow | null;
    pending: boolean;
    onClose: () => void;
    onSave: (body: CollectionBody) => void;
  },
) {
  const [name, setName] = useState(row?.name ?? '');
  // The slug is only editable while it can still be changed: it appears in a URL,
  // and the contract omits it from an update for exactly that reason.
  const [slug, setSlug] = useState(row?.slug ?? '');
  const [state, setState] = useState<CollectionState>(row?.state ?? 'DRAFT');
  const [sortOrder, setSortOrder] = useState(String(row?.sortOrder ?? 0));

  const effectiveSlug = row ? row.slug : (slug || slugify(name));
  const valid = name.trim().length >= 2 && effectiveSlug.length >= 2;

  return (
    <Modal
      title={row ? row.name : 'New collection'}
      onClose={onClose}
      footer={
        <>
          <Button
            size="sm"
            disabled={pending || !valid}
            onClick={() => onSave({
              name: name.trim(),
              slug: effectiveSlug,
              state,
              sortOrder: Number.parseInt(sortOrder, 10) || 0,
            })}
          >
            Save
          </Button>
          <Button size="sm" tone="ghost" onClick={onClose}>Cancel</Button>
        </>
      }
    >
      <Field label="Name">
        <input value={name} onChange={(e) => setName(e.target.value)} />
      </Field>
      <Field
        label="Slug"
        hint={row
          ? 'Fixed. It appears in a URL, so changing it would break every link already sent.'
          : 'Lowercase and hyphenated. Suggested from the name; it cannot be changed afterwards.'}
      >
        {row
          ? <div><code>{row.slug}</code></div>
          : (
            <input
              value={slug}
              placeholder={slugify(name)}
              onChange={(e) => setSlug(slugify(e.target.value))}
            />
          )}
      </Field>
      <div className="mk-fset">
        <Field label="State" hint="A draft collection groups nothing a retailer can see.">
          <select value={state} onChange={(e) => setState(e.target.value as CollectionState)}>
            <option value="DRAFT">Draft</option>
            <option value="PUBLISHED">Published</option>
          </select>
        </Field>
        <Field label="Sort order" hint="Where it sits in the list. Ties fall back to the name.">
          <input value={sortOrder} inputMode="numeric" onChange={(e) => setSortOrder(e.target.value)} />
        </Field>
      </div>
    </Modal>
  );
}

interface CategoryBody {
  name: string;
  slug: string;
  sortOrder: number;
}

function CategoryEditor(
  { row, pending, onClose, onSave }: {
    row: CategoryRow | null;
    pending: boolean;
    onClose: () => void;
    onSave: (body: CategoryBody) => void;
  },
) {
  const [name, setName] = useState(row?.name ?? '');
  const [slug, setSlug] = useState(row?.slug ?? '');
  const [sortOrder, setSortOrder] = useState(String(row?.sortOrder ?? 0));

  const effectiveSlug = row ? row.slug : (slug || slugify(name));
  const valid = name.trim().length >= 2 && effectiveSlug.length >= 2;

  return (
    <Modal
      title={row ? row.name : 'New category'}
      onClose={onClose}
      footer={
        <>
          <Button
            size="sm"
            disabled={pending || !valid}
            onClick={() => onSave({
              name: name.trim(),
              slug: effectiveSlug,
              sortOrder: Number.parseInt(sortOrder, 10) || 0,
            })}
          >
            Save
          </Button>
          <Button size="sm" tone="ghost" onClick={onClose}>Cancel</Button>
        </>
      }
    >
      <Note style={{ marginBottom: 'var(--mk-space-4)' }}>
        A category is what kind of piece it is, and it decides which questions the
        product editor asks: a clasp is only a bracelet's or a chain's, a backing is only
        an earring's.
      </Note>
      <Field label="Name">
        <input value={name} onChange={(e) => setName(e.target.value)} />
      </Field>
      <Field
        label="Slug"
        hint={row
          ? 'Fixed. It appears in a URL, so changing it would break every link already sent.'
          : 'Lowercase and hyphenated. Suggested from the name; it cannot be changed afterwards.'}
      >
        {row
          ? <div><code>{row.slug}</code></div>
          : (
            <input
              value={slug}
              placeholder={slugify(name)}
              onChange={(e) => setSlug(slugify(e.target.value))}
            />
          )}
      </Field>
      <Field label="Sort order" hint="Where it sits in the list. Ties fall back to the name.">
        <input value={sortOrder} inputMode="numeric" onChange={(e) => setSortOrder(e.target.value)} />
      </Field>
    </Modal>
  );
}
